import React, { useCallback, useEffect, useState } from "react";
import styles from '../Dashboard/index.module.css';
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../../components/ToastMsg";
import BeatLoader from "react-spinners/BeatLoader";
import Pagination from "../../../components/Pagination";
import NoResultsFound from "../../../components/NoResultsFound";
import SearchBar from "../../../components/SearchBar";
import TableSkeleton from "../../../components/Skeleton";

import {
    createCertificateType,
    getAllCertificateTypes,
    updateCertificateType,
} from "../../../redux/features/certificateType/certificateTypeService";
import {
    STATUS,
    selectCertificateList,
    selectUpdateCertificateType,
} from "../../../redux/features/certificateType/certificateTypeSlice";
import Modal from "../../../components/Modal";
import { faCircleCheck, faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons/faXmark";

const CertificateType = () => {
    const dispatch = useDispatch();

    const [formData, setFormData] = useState({
        name: '',
        code: '',
    });
    const [errorMessages, setErrorMessages] = useState({});
    const [disabledBtn, setDisabledBtn] = useState(false);
    const [skip, setSkip] = useState(0);
    const [limit] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedType, setSelectedType] = useState(null);
    const [initialType, setInitialType] = useState(null);
    const [editErrorMsgs, setEditErrorMsgs] = useState({
        name: '',
        code: ''
    });

    const [errorMsg, setErrorMsg] = useState(null);
    const [disabledEditBtn, setEditBtnDisable] = useState(false);
    const [isModalOpen, setModalOpen] = useState(false);

    const { status, data: certificateTypes, pageCount } = useSelector(selectCertificateList);
    const isLoading = status === STATUS.LOADING;
    
    const { status: updateStatus, message: updateMsg } = useSelector(selectUpdateCertificateType);
    const isUpdating = updateStatus === STATUS.LOADING;

    const fetchAllCertificateTypes = useCallback(() => {
        dispatch(getAllCertificateTypes({
            skip,
            limit,
            search: searchQuery,
        }));
    }, [dispatch, skip, limit, searchQuery]);

    useEffect(() => {
        fetchAllCertificateTypes();
    }, [fetchAllCertificateTypes]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrorMessages((prev) => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name.trim()) errors.name = "Name is required.";
        if (!formData.code.trim()) {
            errors.code = "Code is required.";
        }
        setErrorMessages(errors);
        return Object.keys(errors).length === 0;
    };

    const handleCreateCertificateType = async () => {
        if (!validateForm()) return;
        setDisabledBtn(true);
        try {
            const response = await dispatch(createCertificateType(formData)).unwrap();
            showToast("success", 'Certificate type created successfully.');
            setFormData({ name: '', code: '' });
            fetchAllCertificateTypes();
        } catch (error) {
            showToast("error", error.message);
        } finally {
            setDisabledBtn(false);
        }
    };

    const handlePageChange = (pageNumber) => {
        setSkip(pageNumber - 1);
    };

    // Status update
    const [isActivateModalOpen, setActivateModalOpen] = useState(false);
    const [isDeactivationModalOpen, setDeactivationModalOpen] = useState(false);
    const [selectedTypeId, setSelectedTypeId] = useState(null);

    const closeModal = () => setActivateModalOpen(false);
    const closDeactivationModal = () => setDeactivationModalOpen(false);

    const handleYes = async () => {
        setActivateModalOpen(false);
        try {
            const response = await dispatch(updateCertificateType({ id: selectedTypeId, status: true })).unwrap();
            setSelectedTypeId(null);
            setInitialType(null);
            showToast('success', response.message);
            fetchAllCertificateTypes();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }

    const handleRejectYes = async () => {
        setDeactivationModalOpen(false);
        try {
            const response = await dispatch(updateCertificateType({ id: selectedTypeId, status: false })).unwrap();
            setSelectedTypeId(null);
            setInitialType(null);
            showToast('success', response.message);
            fetchAllCertificateTypes();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }

    // Edit
        const handleSelectedTypeChange = (e) => {
        const { name, value } = e.target;
        setErrorMsg(null);
        setSelectedType((prev) => ({
            ...prev,
            [name]: value,
        }));

        setEditErrorMsgs((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

     const isValidEditType = () => {
        const errors = {};
        const trimmedName = selectedType.name.trim();

        if (!trimmedName) {
            errors.name = "Name is required.";
        }
        if (!selectedType.code) {
            errors.code = "Code is required.";
        }
        if (Object.keys(errors).length > 0) {
            setEditErrorMsgs(errors);
            return false;
        } else {
            return true;
        }
    };

    function getChangedValues(selectedData) {
        const changedValues = {};
        for (const key in selectedData) {
            if (
                key !== 'id' &&
                selectedData.hasOwnProperty(key) &&
                selectedData[key] !== initialType[key]
            ) {
                changedValues[key] = selectedData[key];
            }

            if (key === 'id') {
                changedValues[key] = selectedData[key];
            }
        }
        return changedValues;
    }

     const editTypeDetails = async () => {
            if (isValidEditType(selectedType)) {
                const data = getChangedValues(selectedType);
                const hasChanges = Object.keys(data).some(key => key !== 'id');
                if (!hasChanges) {
                    setErrorMsg('There are no changes to update.');
                } else {
                    setEditBtnDisable(true);
                    try {
                        const response = await dispatch(updateCertificateType(data)).unwrap();
                        setEditBtnDisable(false);
                        setSelectedType(null);
                        setInitialType(null);
                        setModalOpen(false);
                        showToast('success', response.message);
                        fetchAllCertificateTypes();
                    }
                    catch (error) {
                        setEditBtnDisable(false);
                        showToast('error', error.message);
                    };
                }
            }
        };

    return (
        <div className={`card-section`}>
            <div className={`main-card`}>
                <h1 className={`form-title`}>Add New Certificate Type</h1>
                <div className={styles.formContainer}>
                    <form className={styles.coordinatorForm} noValidate>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='name' className={`form-label`}>
                                        Name<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='name'
                                        name='name'
                                        value={formData.name || ''}
                                        onChange={handleInputChange}
                                        placeholder='Enter Name'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.name && (
                                        <p className={`text-danger`}>{errorMessages.name}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='email' className={`form-label`}>
                                        Code<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='code'
                                        name='code'
                                        value={formData.code || ''}
                                        onChange={handleInputChange}
                                        placeholder='Enter Code'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.code && (
                                        <p className={`text-danger`}>{errorMessages.code}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            className={`btn-primary`}
                            type='button'
                            disabled={disabledBtn}
                            onClick={handleCreateCertificateType}
                        >
                            {disabledBtn ? <BeatLoader color='#ffffff' /> : `Create`}
                        </button>
                    </form>
                </div>
            </div>
            <div className={`search-card`}>
                <div className={`search-card-header`}>
                    <h1 className={`form-title`}>Certificate Types</h1>
                    <SearchBar onSearch={(query) => setSearchQuery(query)} />
                </div>
                <div className={`search-card-body`}>
                    {isLoading ? (
                        <TableSkeleton
                            columns={["Name", "Code", "Status", "Actions"]}
                            rowCount={10}
                            height={20}
                        />
                    ) : certificateTypes.length > 0 ? (
                        <div className={`table-container`}>
                            <table className={`table`}>
                                <thead className={`thead`}>
                                    <tr>
                                        <th className={`theadColumnOne`}>Name</th>
                                        <th>Code</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody className={`tbody`}>
                                    {certificateTypes.map((entity) => (
                                        <tr key={entity.id}>
                                            <td className={`tdataColumnOne`}>{entity.name}</td>
                                            <td>{entity.code}</td>
                                            <td>{entity?.status ? 'Active' : 'Inactive'}</td>
                                            <td className={`actionsBtn`}>
                                                <button className={`btn-edit-label`} onClick={() => {
                                                    setSelectedType(entity);
                                                    setInitialType(entity);
                                                    setEditBtnDisable(false);
                                                    setErrorMsg(null);
                                                    setEditErrorMsgs({
                                                        name: '',
                                                        code: ''
                                                    })
                                                    setModalOpen(true);
                                                }}>Edit</button>
                                                <button
                                                    className={entity.status ? `btn-inactive` : `btn-active`}
                                                    onClick={() => {
                                                        setSelectedTypeId(entity.id);
                                                        entity.status ? setDeactivationModalOpen(true) : setActivateModalOpen(true);
                                                    }
                                                    }>{entity.status ? 'Deactivate' : 'Acitvate'}</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <NoResultsFound />
                    )}
                </div>
                <div className={`search-card-footer`}>
                    {pageCount > 0 && (
                        <div className={styles.paginationContainer}>
                            <Pagination
                                currentPage={skip + 1}
                                totalPages={pageCount}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </div>
            </div>
            {isModalOpen &&
                <div className={`modal-overlay`}>
                    <div className={`modal-card`}>
                        <div className={`modal-header`}>
                            <h4 className={`modal-title`}>Update Certificate Type Details</h4>
                            <FontAwesomeIcon icon={faXmark} onClick={() => setModalOpen(false)} size="lg" cursor="pointer" />
                        </div>
                        <div className={`modal-body`}>
                            <div className={styles.formContainer}>
                                <form
                                    className={styles.coordinatorForm}
                                    noValidate
                                >
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Name<span className={`required-star`}>*</span>
                                            </label>
                                            <input
                                                type='text'
                                                id='full_name'
                                                name='full_name'
                                                value={selectedType.name || ''}
                                                onChange={handleSelectedTypeChange}
                                                placeholder='Enter StaffMember Name'
                                                className={`form-input-default`}
                                            />
                                            {editErrorMsgs.name && (
                                                <p className={`text-danger`}>{editErrorMsgs.name}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Code<span className={`required-star`}>*</span>
                                            </label>
                                            <input
                                                type='text'
                                                id='code'
                                                name='code'
                                                value={selectedType.code || ''}
                                                onChange={handleSelectedTypeChange}
                                                placeholder='Enter Code'
                                                className={`form-input-default`}
                                            />
                                            {editErrorMsgs.code && (
                                                <p className={`text-danger`}>{editErrorMsgs.code}</p>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        className={`btn-primary`}
                                        type='button'
                                        disabled={disabledEditBtn}
                                        onClick={editTypeDetails}
                                    >
                                        {isUpdating? <BeatLoader color='#ffffff' /> : `Save`}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            }
            {isActivateModalOpen && (
                <Modal
                    title='Certificate Type Activation'
                    onClose={closeModal}
                    onYes={handleYes}
                    isInput={false}
                    iconName={faCircleCheck}
                    bodyText={`Are you sure you want to activate the certificate type?`}
                    colorType={`var(--green)`}
                />
            )}
            {isDeactivationModalOpen && (
                <Modal
                    title='Certificate Type Deactivation'
                    onClose={closDeactivationModal}
                    onYes={handleRejectYes}
                    isInput={false}
                    iconName={faCircleXmark}
                    bodyText={`Are you sure you want to deactivate the certificate type?`}
                    colorType={`var(--danger)`}
                />
            )}
        </div>
    );
};

export default CertificateType;